import { useEffect, useState } from 'react';
import {
  ChevronDown,
  Copy,
  FileText,
  Image as ImageIcon,
  ImageOff,
  Loader2,
  Reply,
  RotateCw,
  SmilePlus,
} from 'lucide-react';
import { toast } from 'sonner';

import { ImageLightbox } from '@/components/ImageLightbox';

import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAccounts } from '@/features/accounts/useAccounts';
import { useImageWithMediaToken } from '@/features/auth/useImageWithMediaToken';
import { useAgentsById } from '@/features/agents/useAgentsById';
import { DeliveryStatusIcon } from '@/features/chat/DeliveryStatusIcon';
import {
  RESTRICTED_RESEND_HINT,
  isResendable,
  isRestrictedFailure,
  needsDuplicateRiskConfirm,
} from '@/features/chat/delivery-status';
import { quotedPreviewText, quotedSenderLabel } from '@/features/chat/quote-preview';
import { ResendConfirmDialog } from '@/features/chat/ResendConfirmDialog';
import { useReactToMessage } from '@/features/chat/useReactToMessage';
import { useResendMessage } from '@/features/chat/useResendMessage';
import { useSendMessage } from '@/features/chat/useSendMessage';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/format-time';
import { useAuthStore } from '@/store/auth';

import type { Message, Reaction } from '@/features/chat/chat.api';

/** WhatsApp's quick-reaction six, in WhatsApp's order. The context menu's
 *  「表情回应」 row offers exactly these (free-form emoji input is out of
 *  scope — the platform's own quick bar is the same set). */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;

/** Identity chip naming the OUR-SIDE account (shop number) a message
 *  travelled through — rendered ONLY in merged-thread mode, where bubbles
 *  from several accounts interleave. `dotClassName` is the account's solid
 *  swatch class (`AccountColor.swatch`); `label` is `formatAccountLabel`. */
export interface MessageAccountChip {
  label: string;
  dotClassName: string;
}

/** One alternative resend target for a REAL failed / `unknown` outbound row
 *  — another member conversation of the same customer whose account is
 *  `connected` (merged-thread mode only; `ThreadPanel.resendOptionsFor`
 *  builds these). `colorClass` is the account's solid swatch class
 *  (`AccountColor.swatch`), matching the account-chip / picker dots. */
export interface ResendOption {
  conversationId: string;
  accountId: string;
  label: string;
  colorClass?: string;
}

interface MessageBubbleProps {
  message: Message;
  /** The message `message` is quoting, resolved by `MessageList` from the
   *  currently loaded page window (matched on `platform_message_id`).
   *  `undefined` when `message` isn't a reply, OR when it is but the
   *  quoted original isn't in the loaded window — the quote block falls
   *  back to a neutral "↩ 引用消息" chip in that case. */
  quotedMessage?: Message;
  /** Colored account chip (dot + label) shown in the bubble footer —
   *  passed by `MessageList` only in merged-thread mode; `undefined`/`null`
   *  in the classic single-conversation panel (no chip renders). */
  accountChip?: MessageAccountChip | null;
  /** Alternative resend targets for a REAL failed / `unknown` outbound row —
   *  when non-empty the footer grows a 「用其他账号重发」 menu next to Retry.
   *  `undefined`/`null`/empty (classic single-conversation panel, or no
   *  other connected member) renders Retry alone. */
  resendOptions?: ResendOption[] | null;
  /** The open merged phone-thread's key, when rendering in thread mode —
   *  forwarded to `useResendMessage` so the fresh resent row is mirrored
   *  into the thread's messages cache too. `null`/`undefined` in the
   *  classic single-conversation panel. */
  threadKey?: string | null;
  /** Context-menu "回复" pick — bubbles up to `ChatPanelInner` via
   *  `MessageList`. */
  onReply: (message: Message) => void;
}

/** Format bytes as "1.2 MB" / "245 KB". Tiny helper local to the bubble. */
function formatSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Format an ISO timestamp absolutely for the tooltip. Locale-default. */
function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * One message bubble in the chat history.
 *
 * Layout:
 *   - Inbound: left, neutral background.
 *   - Outbound: right, primary-tinted background.
 *   - Timestamp under the bubble (relative + absolute on hover).
 *   - Status icon for outbound — `DeliveryStatusIcon` (2026-09 honest send
 *     status, spec 02 §3): pending spinner (+ opacity-60 bubble), queued
 *     clock, sent one tick, delivered two ticks, read blue ticks, failed
 *     rose alert (+ rose border, Retry), unknown amber question mark (+
 *     amber border, 「仍要重发…」 behind `ResendConfirmDialog`), and a muted
 *     question mark for any status this console does not know — never a
 *     "sent" tick. Hovering names the status with its receipt time / error.
 *     A failure WhatsApp's reach-out timelock caused adds the explicit hint
 *     「该号被 WhatsApp 限制主动发消息，请用其他账号重发」 under the footer.
 *   - "Sent by {name}" footer for outbound bubbles. Without a proper
 *     agents-by-id query (M5-T7 polish) we fall back to "you" when the
 *     sender id matches the current agent and "agent" otherwise. An
 *     outbound message with NO agent attribution (sender_agent_id null —
 *     the backend's phone-sent provenance rule) is labeled "手机": the
 *     operator sent it from the paired phone, not from this console.
 *   - Media: <img> for image, <audio controls> for audio, a download
 *     link for documents. Media URL = `/api/v1/media/{id}?token=...`.
 *   - Right-click opens a context menu: "回复" starts a reply (hands the
 *     message up to the Composer via `onReply`), "表情回应" opens the
 *     WhatsApp quick six as an inline emoji row (see `QUICK_REACTIONS`),
 *     and "复制" copies `content_text` to the clipboard.
 *   - Reactions render as grouped chips (emoji + count when >1) hanging off
 *     the bubble's bottom corner. The current agent's own reaction is
 *     visually distinct (ring) and its chip is a button — clicking it (or
 *     re-picking the same emoji in the menu) REMOVES the reaction, matching
 *     WhatsApp's toggle semantics. Server truth is one reaction per reactor
 *     per message, so picking a different emoji replaces the old one.
 *   - Reacting requires the target's wamid (`platform_message_id`): the
 *     menu entry is disabled until the platform assigns one (the backend
 *     would 409 otherwise).
 *   - When `message.reply_to_platform_message_id` is set, a quoted-preview
 *     strip renders above the body — the resolved `quotedMessage` when
 *     `MessageList` found it in the loaded window, else a neutral fallback.
 */
export function MessageBubble({
  message,
  quotedMessage,
  accountChip = null,
  resendOptions = null,
  threadKey = null,
  onReply,
}: MessageBubbleProps): React.JSX.Element {
  const isOutbound = message.direction === 'outbound';
  const isPending = message.delivery_status === 'pending';
  const isFailed = message.delivery_status === 'failed';
  // In doubt (spec 02 §3/§4.6): the customer MAY have it — amber, not rose.
  const isUnknown = isOutbound && message.delivery_status === 'unknown';
  const currentAgent = useAuthStore((s) => s.agent);
  const react = useReactToMessage();

  const reactions = message.reactions ?? [];
  const ownReaction = reactions.find(
    (r) => r.reactor_kind === 'agent' && r.reactor_agent_id === currentAgent?.id,
  );
  // WhatsApp addresses a reaction by the TARGET's wamid — nothing to react
  // to until the platform assigns one (a still-queued outbound send).
  const canReact = message.platform_message_id != null;

  function handleCopy(): void {
    void navigator.clipboard.writeText(message.content_text ?? '');
    toast('已复制');
  }

  function handleReact(emoji: string): void {
    // Picking your current emoji again = remove (WhatsApp toggle UX). The
    // hook applies the replace/remove optimistically and rolls back on error.
    const next = ownReaction?.emoji === emoji ? '' : emoji;
    react.mutate({
      conversationId: message.conversation_id,
      messageId: message.id,
      emoji: next,
    });
  }

  return (
    <div
      data-testid="message-bubble"
      className={cn(
        'flex w-full px-6 py-1.5',
        isOutbound ? 'justify-end' : 'justify-start',
      )}
      data-direction={message.direction}
      data-delivery-status={message.delivery_status}
    >
      <div className={cn('flex max-w-[78%] flex-col gap-1', isOutbound && 'items-end')}>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              // Neumorphic bubbles (design §右栏 消息流): incoming = raised
              // #eef1f8 card, 18px radius w/ 6px pointer corner; outgoing =
              // green accent gradient, white text, dark-side drop only.
              className={cn(
                'rounded-[18px] px-4 py-2.5 text-sm font-semibold',
                'transition-opacity',
                isOutbound
                  ? 'neu-gradient rounded-br-[6px] text-white shadow-neu-out'
                  : 'rounded-bl-[6px] bg-[var(--bubble-in)] text-foreground shadow-neu-raised-lg',
                isPending && 'opacity-60',
                isFailed && 'border border-rose-500/40',
                isUnknown && 'border border-amber-500/70',
              )}
            >
              {message.reply_to_platform_message_id && (
                <QuotedPreview quotedMessage={quotedMessage} />
              )}
              <BubbleBody message={message} />
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onSelect={() => onReply(message)}>
              <Reply />
              回复
            </ContextMenuItem>
            <ContextMenuSub>
              <ContextMenuSubTrigger disabled={!canReact}>
                <SmilePlus className="mr-2 size-4" />
                表情回应
              </ContextMenuSubTrigger>
              {/* Inline row of the quick six — each is a ContextMenuItem so
                  picking one closes the menu and keyboard nav works. */}
              <ContextMenuSubContent className="flex min-w-0 flex-row gap-0.5 p-1">
                {QUICK_REACTIONS.map((emoji) => (
                  <ContextMenuItem
                    key={emoji}
                    onSelect={() => handleReact(emoji)}
                    aria-label={`表情回应 ${emoji}`}
                    className={cn(
                      'h-8 w-8 justify-center rounded-md px-0 py-0 text-base',
                      ownReaction?.emoji === emoji && 'bg-accent',
                    )}
                  >
                    {emoji}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
            <ContextMenuItem
              onSelect={handleCopy}
              disabled={!message.content_text}
            >
              <Copy />
              复制
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        {reactions.length > 0 && (
          <ReactionChips
            reactions={reactions}
            ownAgentId={currentAgent?.id ?? null}
            isOutbound={isOutbound}
            onToggleOwn={handleReact}
          />
        )}

        <BubbleFooter
          message={message}
          accountChip={accountChip}
          resendOptions={resendOptions}
          threadKey={threadKey}
        />

        {/* WhatsApp's reach-out timelock NACKed this send: the NUMBER is
            restricted, so a same-account retry cannot fix it — say so
            explicitly (spec 02 §1, §4.5). Own line: the footer row is
            already full. */}
        {isRestrictedFailure(message) && (
          <p
            data-testid="restricted-resend-hint"
            className="px-1 text-[10.5px] font-bold leading-snug text-rose-600"
          >
            {RESTRICTED_RESEND_HINT}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Grouped reaction chips at the bubble's bottom corner.
 *
 * Groups by emoji (first-seen order, count shown when >1). A group that
 * contains the CURRENT agent's reaction is highlighted (primary ring) and
 * rendered as a button whose click removes the reaction — only one's own
 * reaction is clickable; other reactors' chips are inert display.
 */
function ReactionChips({
  reactions,
  ownAgentId,
  isOutbound,
  onToggleOwn,
}: {
  reactions: Reaction[];
  ownAgentId: string | null;
  isOutbound: boolean;
  onToggleOwn: (emoji: string) => void;
}): React.JSX.Element {
  const groups: Array<{ emoji: string; count: number; mine: boolean }> = [];
  for (const reaction of reactions) {
    const mine =
      reaction.reactor_kind === 'agent' && reaction.reactor_agent_id === ownAgentId;
    const existing = groups.find((g) => g.emoji === reaction.emoji);
    if (existing) {
      existing.count += 1;
      existing.mine = existing.mine || mine;
    } else {
      groups.push({ emoji: reaction.emoji, count: 1, mine });
    }
  }

  // Neumorphic: small raised pills hanging off the bubble corner.
  const chipClass =
    'flex items-center gap-0.5 rounded-full border bg-background px-1.5 py-0.5 ' +
    'text-xs leading-none shadow-neu-flat';

  return (
    <div
      data-testid="reaction-chips"
      className={cn(
        // Pull the row up so the chips hang off the bubble's bottom edge.
        '-mt-2.5 z-10 flex flex-wrap gap-1 px-1',
        isOutbound ? 'justify-end' : 'justify-start',
      )}
    >
      {groups.map((group) =>
        group.mine ? (
          <button
            key={group.emoji}
            type="button"
            data-testid="reaction-chip"
            data-mine="true"
            onClick={() => onToggleOwn(group.emoji)}
            title="点击移除我的表情回应"
            className={cn(
              chipClass,
              'cursor-pointer border-primary/50 ring-1 ring-primary/60 hover:bg-accent',
            )}
          >
            <span>{group.emoji}</span>
            {group.count > 1 && <span className="text-muted-foreground">{group.count}</span>}
          </button>
        ) : (
          <span
            key={group.emoji}
            data-testid="reaction-chip"
            className={cn(chipClass, 'border-border')}
          >
            <span>{group.emoji}</span>
            {group.count > 1 && <span className="text-muted-foreground">{group.count}</span>}
          </span>
        ),
      )}
    </div>
  );
}

/**
 * Quoted-message strip rendered above `BubbleBody` when the message is a
 * reply. `quotedMessage` is `undefined` when `MessageList`'s
 * `platform_message_id` lookup missed (the original has scrolled out of
 * the loaded page window, or predates a schema without the field) — in
 * that case we still show that this IS a reply, just without content.
 */
function QuotedPreview({ quotedMessage }: { quotedMessage: Message | undefined }): React.JSX.Element {
  return (
    <div className="mb-1.5 rounded-lg border-l-2 border-current/30 bg-black/5 px-2 py-1 text-xs opacity-80 dark:bg-white/10">
      {quotedMessage ? (
        <>
          <div className="font-medium">{quotedSenderLabel(quotedMessage)}</div>
          <div className="truncate">{quotedPreviewText(quotedMessage)}</div>
        </>
      ) : (
        <div>↩ 引用消息</div>
      )}
    </div>
  );
}

/**
 * Inside the bubble: media (if any), then text (if any). Order is
 * media-then-text — caption style.
 */
function BubbleBody({ message }: { message: Message }): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      {message.media && <MediaPart message={message} />}
      {message.content_text && (
        <p className="whitespace-pre-wrap break-words">{message.content_text}</p>
      )}
      {!message.media && !message.content_text && (
        <p className="italic text-muted-foreground/80">[empty message]</p>
      )}
    </div>
  );
}

/** Shared footprint for the pre-load placeholder AND the "waiting on the
 *  token/bytes" spinner card, so swapping between them (and into the real
 *  `<img>`) never jumps the layout. */
const MEDIA_PLACEHOLDER_CLASSES = 'flex h-32 w-48 items-center justify-center rounded-md bg-background/40 text-xs text-muted-foreground';

/**
 * Media part — picks an inline `<img>`, `<audio>`, or a download link
 * based on `content_type` / mime. The src is built from the message's
 * `media.url` (which is the relative `/api/v1/media/{id}` path the
 * backend returns) plus the short-lived media token as a query param.
 *
 * Optimistic outbound messages carry a local `blob:` URL on `media.url`
 * (see `buildOptimisticMessage`); the hook detects that and skips the
 * token append so the local preview just works.
 *
 * Click-to-load images: a remote image (any `media.url` that is NOT a
 * local `blob:` preview) does not fetch anything — no media token, no
 * image bytes — until the operator clicks it. `useImageWithMediaToken`
 * fires its token query unconditionally the moment it's called, so the
 * only way to gate it without breaking the Rules of Hooks is to mount a
 * DIFFERENT child component once clicked, rather than conditionally
 * calling the hook inside one component. `ClickToLoadImage` owns that
 * gate; `LoadedMedia` is the only place `useImageWithMediaToken` is
 * actually invoked, for images post-click, local blob previews, and the
 * untouched audio/document paths.
 *
 * Token expiry recovery: `useImageWithMediaToken` adds an `onError`
 * handler to the `<img>` that invalidates the cached token and retries
 * once.  If the fresh token also fails the hook surfaces `failed = true`
 * and the bubble renders an "Image unavailable" inline placeholder
 * (`ImageOff` icon) instead of a broken-image chrome default.
 */
function MediaPart({ message }: { message: Message }): React.JSX.Element {
  const media = message.media!;
  const isLocalBlob = media.url.startsWith('blob:');

  // Release the blob URL once the server URL has taken over (i.e. the
  // optimistic placeholder was patched in place). We track the URL we
  // created so a re-render with a new optimistic message doesn't leak.
  // Lives here (not in `LoadedMedia`) because it must run for every media
  // kind regardless of the click gate below — a not-yet-clicked image
  // never carries a blob URL (blob previews skip the gate entirely), but
  // an optimistic document attachment does, and this effect still owns
  // its cleanup.
  useEffect(() => {
    if (!isLocalBlob) return;
    const url = media.url;
    return () => {
      if (url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* noop — best effort */
        }
      }
    };
    // We deliberately want this to fire when `media.url` changes (the
    // optimistic blob URL → server URL transition).
  }, [media.url, isLocalBlob]);

  // Image (or sticker, treated like an image)
  const isImage = (media.mime?.startsWith('image/') ?? false) ||
    message.content_type === 'image' ||
    message.content_type === 'sticker';

  if (isImage && !isLocalBlob) {
    return <ClickToLoadImage message={message} />;
  }

  return <LoadedMedia message={message} />;
}

/**
 * Pre-load placeholder for a remote image. Renders a real `<button>` (not
 * a decorative div) so it's keyboard-reachable and has a meaningful
 * `aria-label`; clicking it flips local `clicked` state, which mounts
 * `LoadedMedia` — the point where the media token (and the image bytes)
 * actually get fetched.
 */
function ClickToLoadImage({ message }: { message: Message }): React.JSX.Element {
  const [clicked, setClicked] = useState(false);

  if (clicked) {
    return <LoadedMedia message={message} />;
  }

  return (
    <button
      type="button"
      onClick={() => setClicked(true)}
      aria-label="加载图片"
      className={cn(
        MEDIA_PLACEHOLDER_CLASSES,
        'flex-col gap-1.5 transition-colors hover:bg-background/60',
      )}
    >
      <ImageIcon className="size-6" />
      <span>点击加载图片</span>
    </button>
  );
}

/**
 * Renders the actual media once it's allowed to load: images that were
 * either a local `blob:` preview or already clicked past `ClickToLoadImage`,
 * plus the untouched audio and document paths. Owns the
 * `useImageWithMediaToken` call — token fetching, src assembly, and the
 * one-shot onError retry.
 */
function LoadedMedia({ message }: { message: Message }): React.JSX.Element {
  const media = message.media!;
  const isLocalBlob = media.url.startsWith('blob:');

  // `useImageWithMediaToken` owns token fetching, src assembly, and the
  // one-shot onError retry logic.  For non-image media types (audio,
  // documents) the token is still fetched here so the src is available;
  // the onError path is only wired to `<img>` elements.
  const { src, onError, failed } = useImageWithMediaToken(message.id, media.url);

  if (!src && !isLocalBlob) {
    return (
      <div className={MEDIA_PLACEHOLDER_CLASSES}>
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  // Image (or sticker, treated like an image)
  const isImage = (media.mime?.startsWith('image/') ?? false) ||
    message.content_type === 'image' ||
    message.content_type === 'sticker';
  if (isImage) {
    // If both retries exhausted, show an inline "Image unavailable" chip
    // instead of the browser's broken-image default.
    if (failed) {
      return (
        <div className="flex items-center gap-1.5 rounded-md bg-background/30 px-2 py-1.5 text-xs text-muted-foreground">
          <ImageOff className="size-4 shrink-0" />
          <span>Image unavailable</span>
        </div>
      );
    }
    if (src) {
      return (
        <ImageLightbox
          src={src}
          alt={media.filename ?? 'attachment'}
          thumbnailClassName="max-h-72 max-w-full rounded-xl object-contain"
          onError={onError}
        />
      );
    }
  }

  // Audio (voice note)
  const isAudio = (media.mime?.startsWith('audio/') ?? false) ||
    message.content_type === 'audio';
  if (isAudio && src) {
    return <audio controls src={src} className="max-w-full" />;
  }

  // Fallback: a download chip for documents / unknown blobs.
  return (
    <a
      href={src ?? '#'}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 rounded-md bg-background/30 px-2 py-1.5 text-xs hover:bg-background/50"
    >
      <FileText className="size-4 shrink-0" />
      <span className="truncate">{media.filename ?? 'attachment'}</span>
      {media.size_bytes != null && (
        <span className="ml-auto shrink-0 opacity-70">
          {formatSize(media.size_bytes)}
        </span>
      )}
    </a>
  );
}

/**
 * Footer under the bubble: relative timestamp (absolute on hover), then
 * for outbound bubbles the delivery icon + "Sent by ..." attribution and
 * retry affordances when failed / in doubt. TWO retry paths, split on row
 * identity:
 *
 *   - GHOST rows (id `optim-...` — the optimistic placeholder whose POST
 *     failed, nothing persisted server-side): the original re-POST retry
 *     via `useSendMessage` (`retryOfMessageId` + same `client_ref`). Text
 *     only — the original File's blob is local to the failed send, so a
 *     ghost media retry still can't reconstruct the bytes (the documented
 *     limitation now confined to this never-persisted case).
 *   - REAL rows (server id) whose status the hub resends — `failed`, or
 *     `unknown` behind a duplicate-risk confirmation (`isResendable`):
 *     `POST /messages/{id}/resend` via `useResendMessage`, which the server
 *     answers by minting a FRESH row and rebuilding media from its
 *     content-addressed store — media survives the retry. Rendered by
 *     `ResendActions` (mounted only for these rows, so the accounts query
 *     it needs never fires for the overwhelmingly-common delivered bubble).
 */
function BubbleFooter({
  message,
  accountChip = null,
  resendOptions = null,
  threadKey = null,
}: {
  message: Message;
  accountChip?: MessageAccountChip | null;
  resendOptions?: ResendOption[] | null;
  threadKey?: string | null;
}): React.JSX.Element {
  const isOutbound = message.direction === 'outbound';
  const currentAgent = useAuthStore((s) => s.agent);
  const { data: agentsById } = useAgentsById();
  const send = useSendMessage();

  // Ghost = the optimistic placeholder id minted by `buildOptimisticMessage`
  // — the row was never persisted, so there is no server row to /resend.
  const isGhost = message.id.startsWith('optim-');

  const senderLabel = (() => {
    if (!isOutbound) return null;
    // Phone-sent provenance rule (Contract C / openclaw-adapter.md):
    // direction='outbound' AND sender_agent_id IS NULL ⇒ the operator sent
    // it from the paired phone's WhatsApp app, not from this console.
    // Optimistic console placeholders never take this branch — they carry
    // the sending agent's id (useSendMessage stamps it at optimistic-write
    // time), so only genuinely agentless outbound rows read as phone-sent.
    if (message.sender_agent_id == null) {
      return '手机';
    }
    if (currentAgent && message.sender_agent_id === currentAgent.id) {
      return 'Sent by you';
    }
    const agent = agentsById?.get(message.sender_agent_id);
    if (agent) {
      return `Sent by ${agent.display_name}`;
    }
    return 'Sent by agent';
  })();

  function handleGhostRetry(): void {
    if (!message.client_ref) return;
    send.mutate({
      conversationId: message.conversation_id,
      text: message.content_text ?? undefined,
      // We can't reconstruct the original File here (the blob is local to
      // the original send) so a GHOST retry only resends text. Real failed
      // rows don't have this limitation anymore — their retry goes through
      // /resend, where the server rebuilds media from its own store.
      file: null,
      clientRef: message.client_ref,
      retryOfMessageId: message.id,
      // Preserve the quote across a retry — the failed placeholder already
      // carries `reply_to_platform_message_id` (stamped at optimistic-write
      // time), so forward it or a failed reply-send would silently resend
      // as a plain message.
      replyTo: message.reply_to_platform_message_id,
    });
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 text-[10.5px] font-bold leading-none text-neu-faint',
        isOutbound ? 'flex-row-reverse' : 'flex-row',
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{formatRelativeTime(message.created_at)}</span>
        </TooltipTrigger>
        <TooltipContent>{formatAbsolute(message.created_at)}</TooltipContent>
      </Tooltip>

      {isOutbound && <DeliveryStatusIcon message={message} />}
      {senderLabel && <span className="opacity-70">· {senderLabel}</span>}

      {/* Merged-thread only: which shop number this message travelled
          through. A tiny color dot + account label, matching the account
          palette used by the sidebar / aggregate badges. */}
      {accountChip && (
        <span
          data-testid="bubble-account-chip"
          className="flex items-center gap-1 opacity-80"
        >
          <span
            aria-hidden="true"
            className={cn('size-2 shrink-0 rounded-[3px]', accountChip.dotClassName)}
          />
          <span className="max-w-[110px] truncate">{accountChip.label}</span>
        </span>
      )}

      {isOutbound && message.delivery_status === 'failed' && isGhost && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleGhostRetry}
          className={RETRY_BUTTON_CLASSES}
          aria-label="Retry sending"
        >
          <RotateCw className="size-3" />
          Retry
        </Button>
      )}

      {isResendable(message) && (
        <ResendActions
          message={message}
          resendOptions={resendOptions}
          threadKey={threadKey}
        />
      )}
    </div>
  );
}

/** Shared footprint for both retry paths' footer buttons (rose: `failed`). */
const RETRY_BUTTON_CLASSES =
  'h-5 gap-1 px-1.5 text-[10px] text-rose-600 hover:text-rose-700';

/** The same footprint in the in-doubt tone (amber: `unknown`). */
const UNKNOWN_RETRY_BUTTON_CLASSES =
  'h-5 gap-1 px-1.5 text-[10px] text-amber-600 hover:text-amber-700 dark:text-amber-400';

/** Where one resend goes. `targetConversationId: null` = the message's own
 *  conversation; `accountLabel` names a cross-account target (the confirm
 *  dialog and the success toast show it). */
interface ResendTarget {
  targetConversationId: string | null;
  accountLabel: string | null;
}

const OWN_CONVERSATION: ResendTarget = { targetConversationId: null, accountLabel: null };

/**
 * Retry affordances for a REAL (persisted) outbound row the hub resends:
 * `failed`, or `unknown` (in doubt — spec 02 §4.5).
 *
 *   - "Retry" resends through the message's OWN conversation via
 *     `POST /messages/{id}/resend` (server-side media rebuild — no client
 *     re-upload, no media loss). Disabled when the accounts cache
 *     POSITIVELY says the row's own account is not `connected` — with the
 *     hint「该账号未连接」on a hoverable WRAPPER span, not on the button:
 *     the Button base class sets `disabled:pointer-events-none`, so a
 *     `title` on the disabled element itself never pops (hover can't reach
 *     it — same reason ThreadPanel's quote-locked picker surfaces its hint
 *     outside the disabled trigger). An unresolvable account (missing
 *     `account_id` on an old cache row, accounts cache still warming) keeps
 *     the button live and lets the Hub's 409 guard + toast carry the news
 *     instead of guessing.
 *   - 「用其他账号重发」(only when `resendOptions` is non-empty — i.e. the
 *     merged thread found other CONNECTED members of the same customer): a
 *     menu of 「用 {label} 重发」 entries, one per member, resending into
 *     that member's conversation. The server drops any quote on these
 *     cross-account resends (reply anchors are per-account wamids).
 *
 * An `unknown` row reads 「仍要重发…」 (amber) instead of "Retry", but the
 * click resends STRAIGHT AWAY, same as `failed` — never a client-side
 * pre-empt. The hub's refusal order puts the duplicate-risk confirm-required
 * 409 LAST, after the LID guard and the not-connected guard (Contract C —
 * `resendMessage`'s doc in chat.api.ts): opening the confirm dialog BEFORE
 * calling the hub used to mean a resend the LID guard (or the not-connected
 * guard) was always going to refuse still made the operator confirm an
 * unrelated duplicate-risk prompt first, only to hit that same refusal on
 * the confirmed retry (FX2-F). `ResendConfirmDialog` opens ONLY when the hub
 * actually answers the confirm-required 409 (`useResendMessage`'s
 * `onConfirmRequired`) — whether that's a row already cached as `unknown`,
 * or one our cache still called `failed` while the server's `unknown` wins.
 * Confirming there resends the SAME target with `confirm_duplicate_risk:
 * true`.
 *
 * Everything is disabled while a resend is in flight — the endpoint 409s a
 * second resend of the same source anyway ("already been resent"), so the
 * disable just keeps the double-click from surfacing as an error toast.
 *
 * Mounted ONLY for resendable rows, so the `useAccounts` query here never
 * fires for ordinary bubbles (same mount-gate idiom as `ClickToLoadImage` /
 * `LoadedMedia` above).
 */
function ResendActions({
  message,
  resendOptions,
  threadKey,
}: {
  message: Message;
  resendOptions: ResendOption[] | null;
  threadKey: string | null;
}): React.JSX.Element {
  // The target waiting for the operator's duplicate-risk confirmation.
  // `open` is separate so the dialog keeps naming its target while it fades
  // out after a close.
  const [confirm, setConfirm] = useState<{ open: boolean; target: ResendTarget }>({
    open: false,
    target: OWN_CONVERSATION,
  });
  const resend = useResendMessage({
    onConfirmRequired: (variables) =>
      setConfirm({
        open: true,
        target: {
          targetConversationId: variables.targetConversationId ?? null,
          accountLabel: variables.accountLabel ?? null,
        },
      }),
  });
  const { data: accounts } = useAccounts();

  const inDoubt = needsDuplicateRiskConfirm(message);
  const buttonClasses = inDoubt ? UNKNOWN_RETRY_BUTTON_CLASSES : RETRY_BUTTON_CLASSES;

  const ownAccount = message.account_id
    ? accounts?.find((a) => a.id === message.account_id)
    : undefined;
  // Disable only on a POSITIVE "resolved and not connected" — see doc above.
  const ownAccountDisconnected =
    ownAccount !== undefined && ownAccount.status !== 'connected';

  const options = resendOptions ?? [];

  function requestResend(target: ResendTarget): void {
    // Always ask the hub first — never pre-empt with the duplicate-risk
    // dialog (FX2-F). The hub checks the confirm-required 409 LAST, after
    // the LID guard and the not-connected guard, so opening the dialog
    // before calling the hub could ask the operator to confirm a duplicate
    // risk for a resend that was actually going to be blocked for an
    // unrelated reason. `onConfirmRequired` (below) opens the dialog only
    // once the hub actually asks for the confirmation.
    resend.mutate({ messageId: message.id, threadKey, ...target });
  }

  function confirmResend(): void {
    resend.mutate({
      messageId: message.id,
      threadKey,
      ...confirm.target,
      confirmDuplicateRisk: true,
    });
    setConfirm((current) => ({ ...current, open: false }));
  }

  const retryButton = (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={() => requestResend(OWN_CONVERSATION)}
      disabled={resend.isPending || ownAccountDisconnected}
      className={buttonClasses}
      aria-label={inDoubt ? '仍要重发…' : 'Retry sending'}
    >
      <RotateCw className="size-3" />
      {inDoubt ? '仍要重发…' : 'Retry'}
    </Button>
  );

  return (
    <>
      {ownAccountDisconnected ? (
        // `disabled:pointer-events-none` on the Button base class means the
        // disabled element itself never receives hover, so a `title` on it
        // would be dead — the hint rides on this genuinely-hoverable
        // wrapper instead (see the component doc above).
        <span
          data-testid="retry-disabled-hint"
          title="该账号未连接"
          className="inline-flex"
        >
          {retryButton}
        </span>
      ) : (
        retryButton
      )}

      {options.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={resend.isPending}
              className={buttonClasses}
              aria-label="用其他账号重发"
            >
              用其他账号重发
              <ChevronDown className="size-3" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[10rem]">
            {options.map((option) => (
              <DropdownMenuItem
                key={option.conversationId}
                disabled={resend.isPending}
                aria-label={`用 ${option.label} 重发`}
                onSelect={() =>
                  requestResend({
                    targetConversationId: option.conversationId,
                    accountLabel: option.label,
                  })
                }
              >
                {option.colorClass && (
                  <span
                    aria-hidden="true"
                    className={cn('size-2 shrink-0 rounded-[3px]', option.colorClass)}
                  />
                )}
                <span className="truncate">用 {option.label} 重发</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ResendConfirmDialog
        open={confirm.open}
        onOpenChange={(open) => setConfirm((current) => ({ ...current, open }))}
        onConfirm={confirmResend}
        targetLabel={confirm.target.accountLabel}
        pending={resend.isPending}
      />
    </>
  );
}
